// Messages between the loader (widget.js, on the host page) and the chat frame (an iframe on the chat host).
//
// Handshake: the frame posts { wfc: 1, t: 'hello', i, p } to its parent with target '*' (it can't know the parent's
// origin yet, and it says nothing secret). The loader accepts it only from its own iframe's window, from the chat
// host's origin, with its instance id; it answers { wfc: 1, t: 'init', i, d } to the chat host's origin with a
// MessagePort. Everything after that goes over the port, which nothing else on the page can read or write.

import type { AppearanceConfig, ClientToolDecl, Identity, Json, Layout, PageInfo, ThemeMode } from './types.ts';

export const PORT_PROTOCOL = 1 as const;

export interface FrameHello {
  wfc: 1;
  t: 'hello';
  /** instance id (from the iframe's ?i=) */
  i: string;
  /** port protocol version */
  p: number;
}

export interface LoaderInit {
  wfc: 1;
  t: 'init';
  i: string;
  d: InitPayload;
}

/** Presentation settings a host page may override (never brain, tools or security settings). */
export interface PresentationOverrides {
  layout?: Layout;
  launcher?: Partial<AppearanceConfig['launcher']>;
  theme?: ThemeMode;
  accentColor?: string;
  locale?: string;
  strings?: Record<string, string>;
}

export interface InitPayload {
  bot: string;
  instance: string;
  visitor: {
    token: string | null;
    conversationId: string | null;
    resumeToken?: string | null;
    /** the character the visitor last switched to */
    character?: string | null;
  };
  user?: Identity;
  context?: Record<string, Json>;
  locale?: string;
  hostLang?: string;
  hostDir?: 'ltr' | 'rtl';
  overrides: PresentationOverrides;
  tools: ClientToolDecl[];
  page: PageInfo;
  display: { mobile: boolean; reducedMotion: boolean; dark: boolean };
  open: boolean;
  preview?: string;
  debug?: boolean;
  /** the public config the loader already fetched (the frame renders with it before its socket connects) */
  config?: unknown;
  /** where the face engine is served from */
  coreBase?: string;
  /** the host page's origin (from the browser, via the message event) */
  embedOrigin?: string;
}

/** Over the port: `id` asks for a reply, `re` is a reply to `id`. */
export interface PortEnvelope<T extends string = string, D = unknown> {
  t: T;
  id?: number;
  re?: number;
  ok?: boolean;
  d?: D;
  err?: { code: string; message: string };
}

export interface LoaderToFrame {
  open: { source: 'launcher' | 'teaser' | 'api' | 'restore'; fullscreen: boolean };
  close: { source: 'launcher' | 'api' | 'frame' | 'escape' };
  page: PageInfo;
  pointer: { x: number; y: number } | null;
  send: { text: string };
  prefill: { text: string };
  'voice.start': { mode?: 'conversation' | 'ptt'; camera?: boolean };
  'voice.stop': Record<string, never>;
  identify: Identity;
  context: { context: Record<string, Json>; merge: boolean };
  update: PresentationOverrides;
  display: { mobile: boolean; reducedMotion: boolean; dark: boolean };
  'tools.set': { tools: ClientToolDecl[] };
  'conversation.new': Record<string, never>;
  'preview.config': { config: unknown };
  shutdown: Record<string, never>;
}

export interface FrameToLoader {
  ready: { conversationId: string | null; visitorId: string; layout: Layout; config: unknown };
  persist: {
    visitorToken: string | null;
    conversationId: string | null;
    resumeToken: string | null;
    character?: string | null;
  };
  'request.open': Record<string, never>;
  'request.close': Record<string, never>;
  resize: { w: number; h: number };
  hit: { rects: Array<{ x: number; y: number; w: number; h: number }> };
  passthrough: { on: boolean };
  unread: { count: number };
  teaser: { text: string };
  'tool.call': { callId: string; name: string; args: Json };
  /** the server gave up on a page tool (the visitor stopped the reply, or it timed out) */
  'tool.cancel': { callId: string };
  navigate: { url: string };
  event: { name: string; data: unknown };
  error: { code: string; message: string; recoverable: boolean };
}

export function isFrameHello(data: unknown): data is FrameHello {
  const d = data as FrameHello;
  return !!d && typeof d === 'object' && d.wfc === 1 && d.t === 'hello' && typeof d.i === 'string';
}

export function isLoaderInit(data: unknown): data is LoaderInit {
  const d = data as LoaderInit;
  return !!d && typeof d === 'object' && d.wfc === 1 && d.t === 'init' && typeof d.i === 'string' && !!d.d;
}

export function isEnvelope(data: unknown): data is PortEnvelope {
  return !!data && typeof data === 'object' && typeof (data as PortEnvelope).t === 'string';
}
